import clsx from 'clsx'
import { Eye, EyeClosed } from 'lucide-react'
import { forwardRef, useState } from 'react'

import { PatternFormat } from 'react-number-format'
import styles from '@/app/styles/components/input.module.scss'
import Typography from './Typograph'

type InputProps = {
  type?:
    | 'text'
    | 'password'
    | 'tel'
    | 'number'
    | 'email'
    | 'date'
    | 'time'
    | 'url'
    | 'search'
    | 'color'
    | 'range'
    | 'file'
  variant?: 'filled' | 'outlined' | 'unstyled'
  label?: string
  floatLabel?: string
  name?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string | string[]
  id?: string
  mask?: string
  value?: string
  max?: string | number
  valueIsNumericString?: boolean
} & React.InputHTMLAttributes<HTMLInputElement>

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      variant = 'filled',
      name,
      label,
      floatLabel,
      placeholder = ' ',
      disabled = false,
      className,
      error,
      id,
      mask,
      value,
      max,
      valueIsNumericString,
      ...rest
    },
    ref,
  ) => {
    const inputId = id || name

    const [inputType, setInputType] = useState(type)

    return (
      <div className={clsx(styles.inputContainer, className)}>
        {label && (
          <Typography variant="p" className="text-neutral-400 mb-2">
            {label}
          </Typography>
        )}

        <div className={styles.inputGroup}>
          {mask ? (
            <PatternFormat
              id={inputId}
              valueIsNumericString={valueIsNumericString}
              name={name}
              placeholder={placeholder}
              disabled={disabled}
              value={value}
              max={max}
              className={clsx(styles.input, {
                [styles.filled]: variant === 'filled',
                [styles.outlined]: variant === 'outlined',
                [styles.unstyled]: variant === 'unstyled',
                [styles.invalid]: error,
              })}
              format={mask}
              onChange={e => {
                rest.onChange?.(e)
              }}
            />
          ) : (
            <input
              ref={ref}
              id={inputId}
              name={name}
              type={inputType}
              placeholder={placeholder}
              disabled={disabled}
              value={value}
              max={max}
              className={clsx(styles.input, {
                [styles.filled]: variant === 'filled',
                [styles.outlined]: variant === 'outlined',
                [styles.unstyled]: variant === 'unstyled',
                [styles.invalid]: error,
              })}
              {...rest}
            />
          )}

          {type === 'password' && (
            <span
              className={styles.eye}
              onClick={() => setInputType(inputType === 'password' ? 'text' : 'password')}
            >
              {inputType === 'password' ? (
                <Eye strokeWidth={1} size={20} className="text-zinc500" />
              ) : (
                <EyeClosed strokeWidth={1} size={20} className="text-zinc500" />
              )}
            </span>
          )}
        </div>

        {floatLabel && <span className={styles.floatLabel}>{floatLabel}</span>}

        {error && (
          <span className={styles.error}>{Array.isArray(error) ? error.join(', ') : error}</span>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export default Input
